/* eslint-disable react/prop-types */
import { CircularProgress, Typography } from '@material-ui/core';
import * as d3 from 'd3';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Cell, Legend, Pie, PieChart, Tooltip } from 'recharts';
import CountyContext from '../../contexts/CountyContext';
import StoryContext from '../../contexts/StoryContext';
import { rdsTabulate } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toFormattedNumber, toPercent } from '../../modules/text';
import CustomTooltip from '../CustomTooltip';
import ResponsiveContainer from '../ResponsiveContainer';

function AccessBySettingTypeSubsidy({ height = 300 }) {
  const { stateCode } = useParams();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const { countyCode, county } = useContext(CountyContext);
  const {
    primaryColor, secondaryColor,
  } = useContext(StoryContext);



  useEffect(() => {
    // const server = new RdsServer(process.env.REACT_APP_RDS_SERVER);

    async function getData() {
      // Instantiate a catalog that exists on the server
      // const catalog = new RdsCatalog(server, 'ccdc');
      // Instantiate a data product that exists on the catalog
      // const dataProduct = new RdsDataProduct(catalog, 'provider');
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND license_type=LIC AND is_subsidized=1 AND (setting_type=HOM OR setting_type=CEN)`;

      if (countyCode) {
        whereClause += ` AND county_fips=${countyCode}`;
      }

      rdsTabulate('ccdc', 'provider', {
        dims: 'setting_type',
        measure: 'count:COUNT(*)',
        where: whereClause,
        format: 'amcharts',
        inject: true,
      })
        .then((result) => {
          const fixedData = result.parsedBody.dataProvider.map((data) => {
            const item = { ...data };
            if (!item.qris_code) {
              item.qris_code = `${stateCode.toUpperCase()}0`;
            }
            return item;
          });
          const d = _.orderBy(fixedData, ['qris_code'], ['asc']);
          const total = d.reduce((a, v) => (a + Number.parseInt(v.count, 10)), 0);
          const payload = d.map((v) => Object.assign(v, { percent: ((v.count / total) * 100) }));
          setData(payload);
          // setData(result.parsedBody.dataProvider);
          // setData([
          //   { name: 'Group A', value: 400 },
          //   { name: 'Group B', value: 300 },
          //   { name: 'Group C', value: 300 },
          //   { name: 'Group D', value: 200 },
          // ])
        })
        .catch((rdsError) => {
          setError(rdsError);
        })
        .finally(() => {
          setReady(true);
        });
    }
    if (stateCode && states[stateCode.toUpperCase()]) {
      // fetch actual average price data
      getData();
    }
  }, [stateCode, countyCode]);

  let title = 'Setting Type';
  if (county) {
    title += ` - ${county} County`;
  }

  const tooltipFormatter = function tooltipFormatter(payload) {
    return `Of the providers who accept subsidies, ${toFormattedNumber(payload.payload.percent)}% are in ${payload.payload.setting_type.toLowerCase().replace('fcc', 'FCC')}.`;
  };

  const colorsGradient = d3.scaleLinear().range([primaryColor, secondaryColor]).domain([0, data.length - 1]);

  if (!ready) {
    return <CircularProgress />;
  }

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <Typography variant="body2" align="center"><strong>{title}</strong></Typography>
      <ResponsiveContainer height={height}>
        <PieChart>
          <Pie

            data={data}
            labelLine
            label={({
              cx,
              cy,
              midAngle,
              innerRadius,
              outerRadius,
              value,
              percent,
            }) => {
              const RADIAN = Math.PI / 180;
              // eslint-disable-next-line
              const radius = 25 + innerRadius + (outerRadius - innerRadius);
              // eslint-disable-next-line
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              // eslint-disable-next-line
              const y = cy + radius * Math.sin(-midAngle * RADIAN);

              return (
                <text
                  x={x}
                  y={y}
                  fill="#000"
                  textAnchor={x > cx ? 'start' : 'end'}
                  dominantBaseline="central"
                >
                  {toPercent(percent / 100)}
                </text>
              );
            }}
            startAngle={0}
            outerRadius={height / 3.2}
            fill="#8884d8"
            dataKey="count"
            nameKey="setting_type"
          >
            {
              data.map((entry, index) => <Cell key={`cell-${entry.setting_type}`} fill={colorsGradient(index)} />)
            }
          </Pie>
          <Legend />
          <Tooltip
            cursor={false}
            content={(
              <CustomTooltip
                formatterFunction={tooltipFormatter}
              />
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </>

  );
}

export default React.memo(AccessBySettingTypeSubsidy);
